import React, { ElementType, forwardRef, useMemo } from 'react';
// import motion and types
import { motion, MotionProps, Transition, Variants } from 'framer-motion';

// import ALL necessary MUI components and their prop types upfront
import LoadingButton, { LoadingButtonProps } from '@mui/lab/LoadingButton';
import { Accordion, AccordionProps, Grid2, Grid2Props } from '@mui/material';
import Box, { BoxProps } from '@mui/material/Box';
import Card, { CardProps } from '@mui/material/Card';
import CardActions, { CardActionsProps } from '@mui/material/CardActions';
import CardHeader, { CardHeaderProps } from '@mui/material/CardHeader';
import CardMedia, { CardMediaProps } from '@mui/material/CardMedia';
import Grid, { GridProps } from '@mui/material/Grid';
import Paper, { PaperProps } from '@mui/material/Paper';
import Stack, { StackProps } from '@mui/material/Stack';
import Typography, { TypographyProps } from '@mui/material/Typography';

export const SpeedDurations = {
  VeryVeryFast: 0.03,
  VeryFast: 0.1,
  Fast: 0.2,
  Normal: 0.5,
  Slow: 1.0,
  VerySlow: 2.0,
} as const;

export type SpeedName = keyof typeof SpeedDurations;

// --- Common Animation Variants (Exported for custom use) ---
export const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};
const defaultStaggerTransitionSettings = {
  delayChildren: 0.1,
  staggerChildren: 0.2,
};
export const containerVariantsWithStagger: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.5, ...defaultStaggerTransitionSettings },
  },
};
export const fadeInSlideUpVariants: Variants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0 },
};
export const fadeInVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};
export const cardEntranceVariants: Variants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0 },
};
export const growFadeInVariants: Variants = {
  hidden: { opacity: 1, scale: 0.01 },
  visible: { opacity: 1, scale: 1 },
};

// --- Helper Function to Create Transition ---
const createTransition = (
  speed: SpeedName = 'Normal',
  staggerSpeed?: SpeedName | null,
  userTransition?: Transition
): Transition | undefined => {
  if (userTransition) return userTransition;
  const baseTransition: Transition = {
    duration: SpeedDurations[speed],
    ease: 'easeOut',
  };
  if (staggerSpeed && SpeedDurations[staggerSpeed]) {
    baseTransition.delayChildren = 0.1;
    baseTransition.staggerChildren = SpeedDurations[staggerSpeed];
  }
  if (baseTransition.duration || baseTransition.staggerChildren) {
    return baseTransition;
  }
  return undefined;
};

// --- Generic Animated Component Factory ---

type BaseAnimatedProps = {
  speed?: SpeedName;
  staggerSpeed?: SpeedName | null;
  transition?: Transition;
  root?: boolean;
  variants?: Variants;
};

// Combine MotionProps, the specific MUI/HTML props (P), and our BaseAnimatedProps
type AnimatedComponentProps<P> = MotionProps & P & BaseAnimatedProps;

/**
 * Factory function to create animated components with default variants and speed control.
 */
function createAnimatedComponent<
  P extends object,
  RefType extends HTMLElement = HTMLDivElement,
>(
  Component: ElementType<P>,
  defaultVariants: Variants,
  displayName: string,
  defaultStagger?: SpeedName | null
) {
  type Props = AnimatedComponentProps<P>;

  const MotionComponent = motion(Component as React.ComponentType<P>);

  const AnimatedComponent = forwardRef<RefType, Props>((props, ref) => {
    const {
      variants = defaultVariants,
      speed = 'Normal',
      staggerSpeed = defaultStagger ?? props.staggerSpeed,
      transition: userTransition,
      root = false,
      initial: userInitial,
      animate: userAnimate,
      ...rest
    } = props;

    const effectiveStaggerSpeed =
      staggerSpeed === null ? undefined : staggerSpeed;
    const transition = useMemo(
      () => createTransition(speed, effectiveStaggerSpeed, userTransition),
      [speed, effectiveStaggerSpeed, userTransition]
    );

    const initial = root ? (userInitial ?? 'hidden') : userInitial;
    const animate = root ? (userAnimate ?? 'visible') : userAnimate;

    return (
      <MotionComponent
        ref={ref}
        variants={variants}
        initial={initial}
        animate={animate}
        transition={transition}
        // Use type assertion on rest spread to bypass complex type check
        {...(rest as any)} // <<< Applied 'as any' here
      />
    );
  });
  AnimatedComponent.displayName = displayName;
  return AnimatedComponent; // Let TS infer return type from forwardRef
}

// --- Create and Export Animated Components using the Factory ---
const defaultItemVariant = fadeInSlideUpVariants;

export const AnimatedStack = createAnimatedComponent<StackProps>(
  Stack,
  fadeInVariants,
  'AnimatedStack'
);
export const AnimatedCard = createAnimatedComponent<CardProps>(
  Card,
  fadeInVariants,
  'AnimatedCard'
);
export const AnimatedCardHeader = createAnimatedComponent<CardHeaderProps>(
  CardHeader,
  fadeInVariants,
  'AnimatedCardHeader'
);
export const AnimatedCardActions = createAnimatedComponent<CardActionsProps>(
  CardActions,
  fadeInVariants,
  'AnimatedCardActions'
);
export const AnimatedDiv = createAnimatedComponent<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>('div', defaultItemVariant, 'AnimatedDiv');
export const AnimatedPaper = createAnimatedComponent<PaperProps>(
  Paper,
  defaultItemVariant,
  'AnimatedPaper'
);
export const AnimatedButton = createAnimatedComponent<
  LoadingButtonProps,
  HTMLButtonElement
>(LoadingButton, fadeInVariants, 'AnimatedButton');
export const AnimatedTypography = createAnimatedComponent<
  TypographyProps,
  HTMLSpanElement
>(Typography, fadeInVariants, 'AnimatedTypography');
export const AnimatedCardMedia = createAnimatedComponent<CardMediaProps>(
  CardMedia,
  fadeInVariants,
  'AnimatedCardMedia'
);
export const AnimatedBox = createAnimatedComponent<BoxProps>(
  Box,
  fadeInVariants,
  'AnimatedBox'
);
export const AnimatedGrid = createAnimatedComponent<GridProps>(
  Grid,
  fadeInVariants,
  'AnimatedGrid'
);
export const AnimatedGrid2 = createAnimatedComponent<Grid2Props>(
  Grid2,
  fadeInVariants,
  'AnimatedGrid2'
);
export const AnimatedAccordion = createAnimatedComponent<AccordionProps>(
  Accordion,
  fadeInVariants,
  'AnimatedAccordion'
);
export const AnimatedSpan = createAnimatedComponent<
  TypographyProps,
  HTMLSpanElement
>(Typography, fadeInVariants, 'AnimatedSpan');

//
